import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useCompleteMentorshipRelationship } from "@/hooks/useMentorshipRelationships";
import { Loader2, PartyPopper, Heart } from "lucide-react";

interface CompleteMentorshipDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  threadId: string;
  otherPersonName: string;
}

export function CompleteMentorshipDialog({
  open,
  onOpenChange,
  threadId,
  otherPersonName,
}: CompleteMentorshipDialogProps) {
  const [note, setNote] = useState("");
  const completeMentorship = useCompleteMentorshipRelationship();

  const handleComplete = async () => {
    await completeMentorship.mutateAsync({
      threadId,
      note: note.trim() || undefined,
    });
    onOpenChange(false);
    setNote("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-center sm:text-center">
          <div className="mx-auto mb-2 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <PartyPopper className="h-8 w-8 text-primary" />
          </div>
          <DialogTitle className="text-2xl">
            🎉 You did it!
          </DialogTitle>
          <DialogDescription className="text-base space-y-3 pt-2">
            <p>
              You've completed a focused mentorship cycle — this is exactly how EAB Connect is designed to work.
            </p>
            <p className="font-medium text-foreground">
              Short bursts. Real progress. Stronger community.
            </p>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="completion-note" className="text-sm">
              What did you get out of this mentorship? <span className="text-muted-foreground">(Optional)</span>
            </Label>
            <Textarea
              id="completion-note"
              placeholder="Share your reflections or wins from this mentorship cycle..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="resize-none"
              rows={3}
            />
          </div>

          <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 text-sm">
            <div className="flex items-start gap-2">
              <Heart className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <p className="text-muted-foreground">
                Completing mentorships helps keep our community generous, healthy, and growing. Your connection with {otherPersonName} continues — you can start another cycle anytime.
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            className="sm:order-1"
          >
            Stay Connected
          </Button>
          <Button 
            onClick={handleComplete} 
            disabled={completeMentorship.isPending}
            className="sm:order-2"
          >
            {completeMentorship.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <PartyPopper className="h-4 w-4 mr-2" />
            )}
            Complete Mentorship
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
